import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { FiActivity, FiAlertTriangle, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import { getManagedUserHistory, ManagedUser, ManagedUserHistory } from './UserManagement.api';
import { Participant } from 'components/History/History.interface';
import { dateFormatHandler, DD_MMM_YYYY } from 'lib/date-formatter-moment';
import { Badge, EmptyState, SkeletonRows, Tone } from 'components/ui';

function Status({ survey }: { survey: Participant }) {
  const statuses: { label: string; tone: Tone }[] = [];
  if (survey.status === 'Complete') statuses.push({ label: 'Completed', tone: 'success' });
  if (survey.status === 'Terminate') statuses.push({ label: 'Terminated', tone: 'danger' });
  if (survey.status === 'OverQuota' || survey.status === 'quotafull') statuses.push({ label: 'Quota full', tone: 'warning' });
  if (survey.status === 'Security' || survey.participantsRedirectUrl?.includes('security')) statuses.push({ label: 'Security Terminated', tone: 'danger' });
  if (!statuses.length) statuses.push({ label: 'Not Found', tone: 'neutral' });
  return <div className="um-history-statuses">{statuses.map(status => <Badge key={status.label} tone={status.tone} dot>{status.label}</Badge>)}</div>;
}

export function UserSurveyHistory({ user, name, onClose }: { user: ManagedUser; name: string; onClose: () => void }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ManagedUserHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setData(null);
    getManagedUserHistory(user.id, page).then(result => { if (active) setData(result); })
      .catch(() => { if (active) setError('Unable to load survey history. Please try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [user.id, page, retry]);
  return <Modal isOpen onRequestClose={onClose} appElement={document.getElementById('root')} contentLabel={`Survey history for ${name}`} className="zs-modal uq-modal um-history-modal" overlayClassName="zs-backdrop uq-overlay">
    <header className="uq-modal-header">
      <div className="min-w-0"><div className="zs-eyebrow">Member activity</div><h2 className="uq-modal-title">Survey history</h2></div>
      <button className="zs-icon-btn shrink-0" aria-label="Close survey history" onClick={onClose}><FiX /></button>
    </header>
    <div className="um-history-member">
      <div className="uq-member">
        <span className="zs-stat-icon"><FiActivity /></span>
        <div className="uq-member-text"><strong>{name}</strong>{user.email && user.email.toLowerCase() !== name.toLowerCase() && <span>{user.email}</span>}<span>Panelist: {user.panelistId || '—'}</span></div>
      </div>
      <span className="zs-badge zs-badge-primary">{loading ? 'Loading activity…' : error ? 'Activity unavailable' : `${Number(data?.totalUsers ?? 0).toLocaleString()} survey attempts`}</span>
    </div>
    {loading ? <div role="status"><span className="sr-only">Loading survey history…</span><SkeletonRows rows={5} /></div>
      : error ? <div role="alert"><EmptyState icon={<FiAlertTriangle />} title="History unavailable" description={error} action={<button className="zs-btn zs-btn-secondary" onClick={() => setRetry(value => value + 1)}>Try again</button>} /></div>
      : !data?.historyList.length ? <EmptyState icon={<FiActivity />} title="No survey history" description="This user has no survey attempts to display." />
      : <div className="zs-table-wrap is-flush uq-table-scroll"><table className="zs-table um-history-table">
        <thead><tr>{['S.No', 'Survey name', 'Points', 'Status', 'Attempt date'].map(label => <th scope="col" key={label} className={label === 'Points' ? 'is-num' : ''}>{label}</th>)}</tr></thead>
        <tbody>{data.historyList.map((survey, index) => <tr key={`${page}-${index}`}>
          <td className="tabular-nums text-ink-3">{(page - 1) * 10 + index + 1}</td>
          <td className="is-strong um-history-name">{survey.surveyName || '—'}</td>
          <td className="is-num is-strong">{Math.floor(Number(survey.points) || 0).toLocaleString()}</td>
          <td><Status survey={survey} /></td>
          <td className="uq-date whitespace-nowrap">{survey.createdAt ? dateFormatHandler(survey.createdAt, DD_MMM_YYYY) : '—'}</td>
        </tr>)}</tbody>
      </table></div>}
    <footer className="zs-table-footer">
      <span>{!loading && !error && data?.totalPages ? `Page ${page} of ${data.totalPages}` : 'Survey activity'}</span>
      <div className="flex gap-2">
        <button className="zs-btn zs-btn-secondary zs-btn-sm" disabled={loading || page === 1} onClick={() => setPage(value => value - 1)}><FiChevronLeft />Previous</button>
        <button className="zs-btn zs-btn-secondary zs-btn-sm" disabled={loading || !!error || page >= (data?.totalPages ?? 0)} onClick={() => setPage(value => value + 1)}>Next<FiChevronRight /></button>
      </div>
    </footer>
  </Modal>;
}
